import React from 'react';
import { Card, Table, Tag, Row, Col, Form, Statistic, Descriptions } from 'antd';
import axios from '../services/axios';
import SelectSearchInput from '../utils/components/selectSearchInput';
import DatePickerCustom from '../utils/components/datePicker';
import moment from 'moment';
import { formatGNF } from '../factures/components/modalDepotForm';

interface Rapport {
  entrees: any[];
  sorties: any[];
  total_gnf: number;
  total_du: number;
  total_benefice: number;
  solde_precedent: number;
  entree_argent: number;
  sortie_argent: number;
  solde_compte: number;
}

const EMPTY: Rapport = {
  entrees: [], sorties: [],
  total_gnf: 0, total_du: 0, total_benefice: 0,
  solde_precedent: 0, entree_argent: 0, sortie_argent: 0, solde_compte: 0,
};

const Benefices = () => {
  const [rapport, setRapport] = React.useState<Rapport>(EMPTY);
  const [clients, setClients] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [dateStart, setDateStart] = React.useState(moment(new Date()).format('YYYY-MM-DD'));
  const [dateEnd, setDateEnd] = React.useState(moment(new Date()).format('YYYY-MM-DD'));
  const [idClient, setIdClient] = React.useState<any>(null);

  React.useEffect(() => {
    fetchRapport(dateStart, dateEnd, null);
    axios.get('/clients').then(response => setClients(response.data));
  }, []);

  const fetchRapport = (start: string, end: string, client: any) => {
    setLoading(true);
    const url = client
      ? `/rapports/benefices/${start}/${end}/${client}`
      : `/rapports/benefices/${start}/${end}`;
    axios.get(url)
      .then(response => {
        setRapport(response.data);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  };

  const onDateChange = (dates: any, dateStrings: any) => {
    setDateStart(dateStrings[0]);
    setDateEnd(dateStrings[1]);
    fetchRapport(dateStrings[0], dateStrings[1], idClient);
  };

  const onClientChange = (value: any) => {
    setIdClient(value);
    fetchRapport(dateStart, dateEnd, value);
  };

  const columns = [
    {
      title: 'N°',
      key: 'index',
      render: (row: any, value: any, index: number = 0) => <>{index + 1}</>,
    },
    { title: "Date d'entrée", dataIndex: 'date_operation', key: 'date_operation' },
    {
      title: 'Déposant',
      key: 'nom_deposant',
      render: (row: any) => <>{row.nom_deposant || `${row.client?.prenom ?? ''} ${row.client?.nom ?? ''}`}</>,
    },
    {
      title: 'Montant reçu',
      dataIndex: 'montant',
      align: 'right',
      key: 'montant',
      render: (montant: any) => formatGNF(montant),
    },
    {
      title: 'Devise',
      key: 'devise',
      align: 'center',
      render: (row: any) => <Tag>{row.devise?.code}</Tag>,
    },
    {
      title: 'Taux du jour',
      dataIndex: 'taux',
      align: 'right',
      key: 'taux',
      render: (taux: any) => Number(taux).toLocaleString('fr-FR', { maximumFractionDigits: 2 }),
    },
    {
      title: 'Montant en GNF',
      dataIndex: 'montant_sortie',
      align: 'right',
      key: 'montant_sortie',
      render: (montant: any) => formatGNF(montant),
    },
    {
      title: 'Taux à payer',
      dataIndex: 'taux_payer',
      align: 'right',
      key: 'taux_payer',
      render: (taux: any, row: any) =>
        Number(taux ?? row.taux).toLocaleString('fr-FR', { maximumFractionDigits: 2 }),
    },
    {
      title: 'Montant dû',
      key: 'montant_du',
      align: 'right',
      render: (row: any) => <>{formatGNF(row.montant_du ?? row.montant_sortie)} GNF</>,
    },
    {
      title: 'Marge bénéficiaire',
      key: 'benefice',
      align: 'right',
      render: (row: any) => {
        const marge = (Number(row.montant_sortie) || 0) - (Number(row.montant_du ?? row.montant_sortie) || 0);
        return <Tag color={marge > 0 ? 'green' : marge < 0 ? 'red' : 'default'}>{formatGNF(marge)} GNF</Tag>;
      },
    },
  ];

  return (
    <Card size="small" title={<h4>Bénéfices et bilan</h4>}>
      <Row justify="end">
        <Form layout="inline">
          <Form.Item label="Dates">
            <DatePickerCustom onChange={onDateChange} />
          </Form.Item>
          <Form.Item label="Compte">
            <SelectSearchInput
              onChange={onClientChange}
              data={clients}
              label="prenom"
              label2="nom"
              label3="telephone"
              valueLabel="id"
              placeholder="Tous les comptes"
            />
          </Form.Item>
        </Form>
      </Row>
      <br />

      <Row gutter={16}>
        <Col xs={24} md={12}>
          <Card size="small" title="Période">
            <Statistic title="Total dû" value={formatGNF(rapport.total_du)} suffix="GNF" valueStyle={{ fontSize: 16 }} />
            <Statistic
              title="Bénéfice"
              value={formatGNF(rapport.total_benefice)}
              suffix="GNF"
              valueStyle={{ color: '#3f8600', fontSize: 16 }}
            />
          </Card>
        </Col>
        <Col xs={24} md={12}>
          <Card size="small" title="Bilan du compte">
            <Descriptions bordered size="small" column={1}>
              <Descriptions.Item label="Solde précédent">
                {formatGNF(rapport.solde_precedent)} GNF
              </Descriptions.Item>
              <Descriptions.Item label="Entrée d'argent">
                {formatGNF(rapport.entree_argent)} GNF
              </Descriptions.Item>
              <Descriptions.Item label="Sortie d'argent">
                {formatGNF(rapport.sortie_argent)} GNF
              </Descriptions.Item>
              <Descriptions.Item label="Solde de compte">
                <b>{formatGNF(rapport.solde_compte)} GNF</b>
              </Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
      <br />

      <Table
        columns={columns as any}
        rowKey={(record: any) => record.id}
        dataSource={rapport.entrees}
        pagination={{ pageSize: 100 }}
        loading={loading}
        scroll={{ x: 'max-content' }}
        summary={() => (
          <Table.Summary fixed>
            <Table.Summary.Row>
              <Table.Summary.Cell index={0} colSpan={6}><b>Total</b></Table.Summary.Cell>
              <Table.Summary.Cell index={6} align="right">
                <b>{formatGNF(rapport.total_gnf)} GNF</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={7} />
              <Table.Summary.Cell index={8} align="right">
                <b>{formatGNF(rapport.total_du)} GNF</b>
              </Table.Summary.Cell>
              <Table.Summary.Cell index={9} align="right">
                <b>{formatGNF(rapport.total_benefice)} GNF</b>
              </Table.Summary.Cell>
            </Table.Summary.Row>
          </Table.Summary>
        )}
      />
    </Card>
  );
};

export default Benefices;
